import React, { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import './Products.css';

function Products() {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [url, setUrl] = useState('');
  const [productName, setProductName] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [alertPrice, setAlertPrice] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editUrl, setEditUrl] = useState('');
  const [editProductName, setEditProductName] = useState('');
  const [editProductPrice, setEditProductPrice] = useState('');
  const [editAlertPrice, setEditAlertPrice] = useState('');
  const [urlValidated, setUrlValidated] = useState(false);
  const [fetchingPrice, setFetchingPrice] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('Fetching price...');

  useEffect(() => {
    // Check if user is logged in
    const checkUserAndLoadProducts = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        window.location.href = '/';
        return;
      }
      await loadProducts();
    };

    checkUserAndLoadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        window.location.href = '/';
        return;
      }

      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      setProducts(data || []);
    } catch (err) {
      setError('Error loading products: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleValidateUrl = async () => {
    if (!url || !url.startsWith('http')) {
      setError('Please enter a valid URL starting with http:// or https://');
      return;
    }

    console.log('Validating and fetching price for URL:', url);
    setFetchingPrice(true);
    setLoadingMessage('Fetching price...');
    setError(null);
    
    // Progressive loading messages
    const messageTimeouts = [
      { time: 10000, message: "Just having to look a bit further." },
      { time: 20000, message: "Still working on it..." },
      { time: 40000, message: "Don't worry, this can take some time" },
      { time: 60000, message: "These guys really don't want us tracking their prices, but we're on it!" },
      { time: 90000, message: "This should usually take less than 2 minutes...maybe we need to include podcasts here 🤔" }
    ];
    
    let timeoutIds = [];

    try {
      // Set up progressive messages
      timeoutIds = messageTimeouts.map(({ time, message }) => 
        setTimeout(() => setLoadingMessage(message), time)
      );
      const response = await fetch(`/api/fetch_price?url=${encodeURIComponent(url)}`);
      console.log('Response status:', response.status);
      
      const contentType = response.headers.get('content-type');
      let data;
      
      if (contentType && contentType.includes('application/json')) {
        data = await response.json();
        console.log('Response data:', data);
      } else {
        const text = await response.text();
        console.error('Non-JSON response:', text.substring(0, 200));
        throw new Error(`Server returned ${response.status}: ${text.substring(0, 100)}`);
      }

      if (response.status === 404 && data.error === 'STORE_NOT_SUPPORTED') {
        // Store not supported - show specific message
        const baseUrl = data.base_url || new URL(url).origin;
        setError(
          `Cart Scanners is not yet able to scan prices from ${baseUrl}. ` +
          `Please refer to our list of retailers on the home page to see where you can track prices from.`
        );
        setUrlValidated(false); // Don't enable fields for unsupported stores
      } else if (response.status === 404 && data.error) {
        // No selector found, but still enable fields and let them enter manually
        setError('Could not automatically fetch price. You can enter it manually.');
        setUrlValidated(true);
      } else if (data.price_found && data.price_text) {
        // Extract number from price text (e.g., "CA $ 59.90" -> "59.90")
        const priceMatch = data.price_text.match(/[\d.,]+/);
        if (priceMatch) {
          const cleanPrice = priceMatch[0].replace(/,/g, '');
          setProductPrice(cleanPrice);
        }
        setUrlValidated(true);
        setSuccess('Price fetched successfully!');
      } else {
        // No price found, but enable fields for manual entry
        setError(data.error || 'Could not automatically fetch price. Please enter manually.');
        setUrlValidated(true);
      }
    } catch (err) {
      console.error('Error fetching price:', err);
      setError('Error fetching price: ' + err.message + '. You can still enter the price manually.');
      setUrlValidated(true); // Still enable fields even if fetch failed
    } finally {
      // Clear all message timeouts
      timeoutIds.forEach(timeoutId => clearTimeout(timeoutId));
      setFetchingPrice(false);
      setLoadingMessage('Fetching price...');
    }
  };


  const handleAddProduct = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (!url || !productName || !alertPrice) {
        throw new Error('Please fill in URL, Product Name, and Alert Price');
      }

      if (!productPrice) {
        throw new Error('Please fetch or enter the current product price');
      }

      const productPriceNum = parseFloat(productPrice);
      const alertPriceNum = parseFloat(alertPrice);
      
      if (isNaN(productPriceNum) || productPriceNum <= 0) {
        throw new Error('Product price must be a valid positive number');
      }
      
      if (isNaN(alertPriceNum) || alertPriceNum <= 0) {
        throw new Error('Alert price must be a valid positive number');
      }

      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        throw new Error('You must be logged in to add products');
      }

      const { error } = await supabase
        .from('products')
        .insert([
          {
            user_id: user.id,
            url: url.trim(),
            product_name: productName.trim(),
            product_price: productPriceNum,
            alert_price: alertPriceNum
          }
        ]);

      if (error) throw error;

      setSuccess('Product added successfully!');
      setUrl('');
      setProductName('');
      setProductPrice('');
      setAlertPrice('');
      setUrlValidated(false);
      setShowAddForm(false);
      await loadProducts();
    } catch (err) {
      setError(err.message);
    }
  };

  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to delete this product?')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;

      setSuccess('Product deleted successfully!');
      await loadProducts();
    } catch (err) {
      setError('Error deleting product: ' + err.message);
    }
  };

  const handleStartEdit = (product) => {
    setEditingId(product.id);
    setEditUrl(product.url);
    setEditProductName(product.product_name || '');
    setEditProductPrice(product.product_price ? product.product_price.toString() : '');
    setEditAlertPrice(product.alert_price ? product.alert_price.toString() : '');
    setError(null);
    setSuccess(null);
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditUrl('');
    setEditProductName('');
    setEditProductPrice('');
    setEditAlertPrice('');
    setError(null);
    setSuccess(null);
  };

  const handleUpdateProduct = async (e, productId) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    try {
      if (!editUrl || !editProductName || !editProductPrice || !editAlertPrice) {
        throw new Error('Please fill in URL, Product Name, Product Price, and Alert Price');
      }

      const productPriceNum = parseFloat(editProductPrice);
      const alertPriceNum = parseFloat(editAlertPrice);
      
      if (isNaN(productPriceNum) || productPriceNum <= 0) {
        throw new Error('Product price must be a valid positive number');
      }
      
      if (isNaN(alertPriceNum) || alertPriceNum <= 0) {
        throw new Error('Alert price must be a valid positive number');
      }

      const { error } = await supabase
        .from('products')
        .update({
          url: editUrl.trim(),
          product_name: editProductName.trim(),
          product_price: productPriceNum,
          alert_price: alertPriceNum
        })
        .eq('id', productId);

      if (error) throw error;

      setSuccess('Product updated successfully!');
      setEditingId(null);
      setEditUrl('');
      setEditProductName('');
      setEditProductPrice('');
      setEditAlertPrice('');
      await loadProducts();
    } catch (err) {
      setError(err.message);
    }
  };

  if (loading) {
    return (
      <div className="products-container">
        <div className="products-content">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="products-container">
      {/* Loading Modal */}
      {fetchingPrice && (
        <div className="loading-overlay">
          <div className="loading-modal">
            <img 
              src="/static/loading.gif" 
              alt="Loading" 
              className="loading-gif"
            />
            <p className="loading-message">{loadingMessage}</p>
          </div>
        </div>
      )}
      <div className="products-content">
        <div className="products-header">
          <h1>My Products</h1>
          <button
            className="add-product-button"
            onClick={() => {
              setShowAddForm(!showAddForm);
              setError(null);
              setSuccess(null);
              if (!showAddForm) {
                // Reset form when opening
                setUrl('');
                setProductName('');
                setProductPrice('');
                setAlertPrice('');
                setUrlValidated(false);
              }
            }}
          >
            {showAddForm ? 'Cancel' : '+ Add New Product'}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {showAddForm && (
          <form onSubmit={handleAddProduct} className="add-product-form">
            <div className="form-group">
              <label htmlFor="url">Product URL</label>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <input
                  id="url"
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  placeholder="https://example.com/product"
                  required
                  disabled={fetchingPrice || urlValidated}
                  style={{ flex: 1 }}
                />
                {!urlValidated && (
                <button
                  type="button"
                    onClick={handleValidateUrl}
                  disabled={!url || !url.startsWith('http') || fetchingPrice}
                  style={{
                    padding: '0.5rem 1rem',
                    background: '#FE7E4F',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '0.9rem',
                    fontWeight: '600',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#ff6b3a'}
                  onMouseLeave={(e) => e.target.style.background = '#FE7E4F'}
                >
                    {fetchingPrice ? 'Fetching...' : 'OK'}
                </button>
                )}
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="productName">Product Name</label>
              <input
                id="productName"
                type="text"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                placeholder="e.g., Men's T-Shirt"
                required
                disabled={!urlValidated}
                style={{ opacity: urlValidated ? 1 : 0.5, cursor: urlValidated ? 'text' : 'not-allowed' }}
              />
              {!urlValidated && (
                <small style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                  Enter and validate URL first
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="productPrice">Current Product Price</label>
              <input
                id="productPrice"
                type="number"
                step="0.01"
                min="0"
                value={productPrice}
                onChange={(e) => setProductPrice(e.target.value)}
                placeholder="0.00 (fetched automatically)"
                required
                disabled={!urlValidated}
                style={{ opacity: urlValidated ? 1 : 0.5, cursor: urlValidated ? 'text' : 'not-allowed' }}
              />
              <small style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                {urlValidated 
                  ? 'Filled automatically from URL, or enter manually' 
                  : 'Will be filled automatically when URL is validated'}
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="alertPrice">Alert When Price Drops To</label>
              <input
                id="alertPrice"
                type="number"
                step="0.01"
                min="0"
                value={alertPrice}
                onChange={(e) => setAlertPrice(e.target.value)}
                placeholder="0.00"
                required
                disabled={!urlValidated}
                style={{ opacity: urlValidated ? 1 : 0.5, cursor: urlValidated ? 'text' : 'not-allowed' }}
              />
              {!urlValidated && (
                <small style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                  Enter and validate URL first
                </small>
              )}
              {urlValidated && (
                <small style={{ fontSize: '0.85rem', color: '#666', display: 'block', marginTop: '0.25rem' }}>
                  You'll be notified when the product price drops to or below this amount
                </small>
              )}
            </div>

            <button type="submit" className="submit-button">
              Add Product
            </button>
          </form>
        )}


        <div className="products-list">
          {products.length === 0 ? (
            <div className="empty-state">
              <p>No products saved yet.</p>
              <p>Click "Add New Product" to get started!</p>
            </div>
          ) : (
            <div className="products-grid">
              {products.map((product) => (
                <div key={product.id} className="product-card">
                  {editingId === product.id ? (
                    <form 
                      onSubmit={(e) => handleUpdateProduct(e, product.id)} 
                      className="edit-product-form"
                    >
                      <div className="form-group">
                        <label htmlFor={`edit-url-${product.id}`}>Product URL</label>
                        <input
                          id={`edit-url-${product.id}`}
                          type="url"
                          value={editUrl}
                          onChange={(e) => setEditUrl(e.target.value)}
                          placeholder="https://example.com/product"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`edit-product-name-${product.id}`}>Product Name</label>
                        <input
                          id={`edit-product-name-${product.id}`}
                          type="text"
                          value={editProductName}
                          onChange={(e) => setEditProductName(e.target.value)}
                          placeholder="e.g., Men's T-Shirt"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`edit-product-price-${product.id}`}>Current Product Price</label>
                        <input
                          id={`edit-product-price-${product.id}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={editProductPrice}
                          onChange={(e) => setEditProductPrice(e.target.value)}
                          placeholder="0.00"
                          required
                        />
                      </div>

                      <div className="form-group">
                        <label htmlFor={`edit-alert-price-${product.id}`}>Alert Price</label>
                        <input
                          id={`edit-alert-price-${product.id}`}
                          type="number"
                          step="0.01"
                          min="0"
                          value={editAlertPrice}
                          onChange={(e) => setEditAlertPrice(e.target.value)}
                          placeholder="0.00"
                          required
                        />
                      </div>

                      <div className="edit-actions">
                        <button type="submit" className="save-button">
                          Save
                        </button>
                        <button 
                          type="button" 
                          className="cancel-button"
                          onClick={handleCancelEdit}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <div className="product-header">
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem' }}>
                          <div style={{ flex: 1 }}>
                            {product.product_name && (
                              <div className="product-name">
                                {product.product_name}
                              </div>
                            )}
                            <a
                              href={product.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="product-url"
                            >
                              {product.url}
                            </a>
                          </div>
                          <div className="product-actions">
                          <button
                            className="edit-button"
                            onClick={() => handleStartEdit(product)}
                            title="Edit product"
                          >
                            ✏️
                          </button>
                          <button
                            className="delete-button"
                            onClick={() => handleDeleteProduct(product.id)}
                            title="Delete product"
                          >
                            ×
                          </button>
                          </div>
                        </div>
                      </div>
                      <div className="product-price">
                        <div>
                          <strong>Current Price</strong>
                          <span className="current-price">${parseFloat(product.product_price || 0).toFixed(2)}</span>
                        </div>
                        <div>
                          <strong>Alert At</strong>
                          <span className="alert-price">${parseFloat(product.alert_price || 0).toFixed(2)}</span>
                        </div>
                        {product.product_price && product.alert_price && product.product_price <= product.alert_price && (
                          <div className="price-alert-triggered">
                            🎉 Price Alert Triggered!
                          </div>
                        )}
                      </div>
                      <div className="product-date">
                        Added {new Date(product.created_at).toLocaleDateString()}
                      </div>
                    </>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Products;

